import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, FileSpreadsheet, Loader2, Mail, Printer } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SupplierPicker } from "./SupplierPicker";
import { SupplierPrintSheet } from "./SupplierPrintSheet";
import { SupplierDispatchHistory } from "./SupplierDispatchHistory";
import { supplierRows } from "./supplierExport";

const SECTION_LABELS = {
  cabinet_boxes: "Cabinet & Drawer Boxes",
  doors_fronts: "Doors & Fronts",
  gables_mouldings: "Gables & Mouldings",
  gables_panels: "Gables & Panels",
  crown_moulding: "Crown Moulding",
  light_valance: "Light Valance",
  riser_fascia: "Riser & Fascia",
};

export const SupplierExportToolbar = ({ sheet, suppliers, setSuppliers, selectedIds, onDispatched, canDispatch, dirty, notes, compact = false }) => {
  const [supplierId, setSupplierId] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const sectionOptions = useMemo(() => Object.entries(sheet.sections || {}).map(([key, sectionRows]) => ({
    key,
    label: SECTION_LABELS[key] || key.replaceAll("_", " ").replace(/\b\w/g, letter => letter.toUpperCase()),
    rowIds: sectionRows.filter(row => selectedIds.has(row.row_id)).map(row => row.row_id),
  })).filter(section => section.rowIds.length), [sheet.sections, selectedIds]);
  useEffect(() => {
    const available = new Set(sectionOptions.map(section => section.key));
    setSelectedSections(current => current.filter(section => available.has(section)));
  }, [sectionOptions]);
  const ids = useMemo(() => sectionOptions.filter(section => selectedSections.includes(section.key)).flatMap(section => section.rowIds), [sectionOptions, selectedSections]);
  const rows = useMemo(() => supplierRows(sheet.sections, new Set(ids)), [sheet.sections, ids]);
  const query = new URLSearchParams({ row_ids: ids.join(","), section_ids: selectedSections.join(","), ...(supplierId ? { supplier_id: supplierId } : {}) });
  const pdfUrl = `${API}/production-sheets/${sheet.sheet_id}/supplier-manifest.pdf?${query.toString()}`;
  const xlsxUrl = `${API}/production-sheets/${sheet.sheet_id}/supplier-manifest.xlsx?${query.toString()}`;

  const requireRows = () => {
    if (!rows.length) { toast.error("Tick the manufacturing rows first"); return false; }
    return true;
  };
  const print = () => {
    if (!requireRows()) return;
    const cleanup = () => document.body.classList.remove("supplier-manifest-printing");
    document.body.classList.add("supplier-manifest-printing");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    window.setTimeout(cleanup, 1500);
  };
  const download = () => {
    if (!requireRows()) return;
    if (!supplierId) return toast.error("Choose a supplier for this PDF");
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };
  const downloadExcel = () => {
    if (!requireRows()) return;
    if (!supplierId) return toast.error("Choose a supplier for this Excel file");
    window.open(xlsxUrl, "_blank", "noopener,noreferrer");
  };
  const email = async () => {
    if (!requireRows()) return;
    if (!supplierId) return toast.error("Choose a supplier");
    if (dirty) return toast.error("Wait for the sheet to finish saving");
    setSending(true);
    try {
      const response = await axios.post(`${API}/production-sheets/${sheet.sheet_id}/dispatch`, { supplier_id: supplierId, row_ids: ids, section_ids: selectedSections, notes: notes || null });
      toast.success(response.data.dispatch.email_sent ? `PDF + Excel emailed to ${response.data.dispatch.supplier_name}` : "Dispatch recorded with both files — SMTP did not send");
      onDispatched(response.data.sheet);
    } catch (error) { toast.error(error.response?.data?.detail || "Could not email supplier PDF"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid="supplier-export-toolbar">
      <div className="grid gap-2 sm:grid-cols-[minmax(190px,1fr)_auto]">
        <SupplierPicker
          suppliers={suppliers}
          value={supplierId}
          onChange={setSupplierId}
          disabled={!canDispatch}
          testId="supplier-export-picker"
          onCreated={supplier => setSuppliers(previous => [...previous, supplier].sort((a, b) => a.company_name.localeCompare(b.company_name)))}
        />
        <span className="self-center text-right text-xs text-slate-500" data-testid="supplier-export-selection-count">{rows.length} included · {selectedIds.size} checked</span>
      </div>
      {sectionOptions.length > 0 && <div className="border-y border-slate-200 py-2" data-testid="supplier-worksheet-selector">
        <p className="mb-1.5 text-[11px] font-semibold uppercase text-slate-500">Worksheets to include</p>
        <div className="flex flex-wrap gap-2">{sectionOptions.map(section => {
          const checked = selectedSections.includes(section.key);
          return <label key={section.key} className={`flex cursor-pointer items-center gap-2 border px-2.5 py-1.5 text-xs transition-colors ${checked ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-600"}`} data-testid={`supplier-worksheet-option-${section.key}`}>
            <Checkbox checked={checked} onCheckedChange={value => setSelectedSections(current => value === true ? [...current, section.key] : current.filter(item => item !== section.key))} data-testid={`supplier-worksheet-checkbox-${section.key}`} />
            <span>{section.label}</span><span className="font-mono text-slate-400">{section.rowIds.length}</span>
          </label>;
        })}</div>
      </div>}
      <div className={`grid gap-1.5 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4"}`}>
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={print} disabled={!rows.length} data-testid="print-selected-button"><Printer className="mr-1.5 h-4 w-4" />Print Selected</Button>
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={download} disabled={!rows.length || !supplierId || dirty} data-testid="download-supplier-pdf-button"><Download className="mr-1.5 h-4 w-4" />Download Manifest</Button>
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={downloadExcel} disabled={!rows.length || !supplierId || dirty} data-testid="download-supplier-xlsx-button"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Download Excel Data</Button>
        <Button size="sm" className="w-full justify-start" onClick={email} disabled={!canDispatch || sending || !rows.length || !supplierId || dirty} data-testid="email-supplier-pdf-button">{sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}Email to Supplier</Button>
      </div>
      <SupplierPrintSheet rows={rows} />
      <SupplierDispatchHistory sheet={sheet} />
    </div>
  );
};